import { Close, DownloadOutlined } from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Button,
  Chip,
  Container,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useEffect, useState } from 'react'
import ClipboardIllustration from '../Misc/ClipboardIllustration'
import dayjs from 'dayjs'
import './CogsInvoice.css'

function CogsInvoice() {
  const [inputFile, setInputFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [generateError, setGenerateError] = useState<boolean>(false)
  const [downloadError, setDownloadError] = useState<boolean>(false)
  const [reportData, setReportData] = useState<
    {
      creditMemoNumber: string
      invoiceNumber: string
      itemName: string
      quantity: string
      unitCost: string
      vendorInv: string
      cogsAccount: string
      date: string
    }[]
  >([])

  useEffect(() => {
    document.title = 'COGS Adjustment Invoice Report | Salestrack'
  }, [])

  const handleGenerateReport = () => {
    setLoading(true)
    if (inputFile) {
      setFileName(inputFile.name)
      const formData = new FormData()
      formData.append('cogsInput', inputFile)
      axios
        .post('/report/cogsInvoice', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(res => {
          setGenerateError(false)
          setReportData(res.data)
        })
        .catch(err => {
          setReportData([])
          console.error(err)
          setGenerateError(true)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const handleDownloadReport = () => {
    setDownloading(true)
    axios
      .post('/report/cogsInvoice/download', { data: reportData }, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `COGS_Adjustment_Invoice_Report_${dayjs().format('MM-DD-YYYY')}.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        setDownloadError(false)
      })
      .catch(err => {
        setDownloadError(true)
        console.error(err)
      })
      .finally(() => {
        setDownloading(false)
      })
  }

  return (
    <div className="cogs-invoice">
      <Container maxWidth={false} className="cogs-invoice-header">
        <Typography variant="h6">Generate Report</Typography>
        <div className="cogs-invoice-header-file">
          <div className="cogs-invoice-header-file-input">
            <Typography variant="body2">{inputFile ? inputFile.name : 'Upload an Excel or CSV file'}</Typography>
            <Button variant="outlined" size="small" component="label">
              Choose File
              <input
                type="file"
                hidden
                onChange={e => {
                  if (e.target.files && e.target.files.length > 0) {
                    setInputFile(e.target.files[0])
                  } else {
                    setInputFile(null)
                  }
                }}
              />
            </Button>
          </div>
          <Button
            variant="contained"
            size="small"
            onClick={handleGenerateReport}
            disabled={
              !inputFile ||
              !(inputFile.name.endsWith('.xlsx') || inputFile.name.endsWith('.xls') || inputFile.name.endsWith('.csv'))
            }
            loading={loading}
          >
            Generate Report
          </Button>
        </div>
      </Container>
      {reportData.length === 0 ? (
        <div className="cogs-invoice-blank">
          <ClipboardIllustration />
          <Typography variant="h5">Report Not Generated</Typography>
          <Typography color="text.secondary">Upload an Excel or CSV file to generate a report.</Typography>
        </div>
      ) : (
        <TableContainer>
          <div className="cogs-invoice-table-title">
            <div className="cogs-invoice-table-title-left">
              <Typography variant="h6">COGS Adjustment Invoice Report</Typography>
              <Chip label={fileName} />
              <Button
                variant="contained"
                startIcon={<DownloadOutlined />}
                onClick={handleDownloadReport}
                loading={downloading}
              >
                Download
              </Button>
            </div>
            <TablePagination
              component="div"
              count={reportData.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, value) => setPage(value)}
              onRowsPerPageChange={e => setRowsPerPage(parseInt(e.target.value))}
            />
          </div>
          <Table stickyHeader color="primary">
            <TableHead>
              <TableRow>
                <TableCell>Credit Memo Number</TableCell>
                <TableCell>Invoice Number</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Unit Cost</TableCell>
                <TableCell>Vendor Inv</TableCell>
                <TableCell>CoGs Account</TableCell>
                <TableCell align="right" width="5%">
                  Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.creditMemoNumber}</TableCell>
                  <TableCell>{row.invoiceNumber}</TableCell>
                  <TableCell>{row.itemName}</TableCell>
                  <TableCell align="right">{row.quantity}</TableCell>
                  <TableCell align="right">
                    {row.unitCost
                      ? row.unitCost
                          .split(',')
                          .map(num => '$' + num)
                          .join(', ')
                      : null}
                  </TableCell>
                  <TableCell>{row.vendorInv}</TableCell>
                  <TableCell>{row.cogsAccount}</TableCell>
                  <TableCell align="right">{row.date}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={reportData.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, value) => setPage(value)}
            onRowsPerPageChange={e => setRowsPerPage(parseInt(e.target.value))}
          />
        </TableContainer>
      )}
      <Snackbar open={generateError} onClose={() => setGenerateError(false)}>
        <Alert
          severity="error"
          onClose={() => setGenerateError(false)}
          action={
            <>
              <Button color="inherit" size="small" loading={loading} onClick={handleGenerateReport}>
                Retry
              </Button>
              <IconButton color="inherit" size="small" onClick={() => setGenerateError(false)}>
                <Close fontSize="small" />
              </IconButton>
            </>
          }
        >
          <AlertTitle>Error Generating Report</AlertTitle>
          There was an error generating the report. Please try again or contact support if the error persists.
        </Alert>
      </Snackbar>
      <Snackbar open={downloadError} onClose={() => setDownloadError(false)}>
        <Alert
          severity="error"
          onClose={() => setDownloadError(false)}
          action={
            <>
              <Button color="inherit" size="small" loading={downloading} onClick={handleDownloadReport}>
                Retry
              </Button>
              <IconButton color="inherit" size="small" onClick={() => setDownloadError(false)}>
                <Close fontSize="small" />
              </IconButton>
            </>
          }
        ></Alert>
      </Snackbar>
    </div>
  )
}

export default CogsInvoice
